import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errorMessage, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminAccess() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: admins = [] } = useQuery({ queryKey: ["admins"], queryFn: () => api.get("/admin/admins").then((r) => r.data) });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admins"] });

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/admins", { email: email.trim() }); toast.success(`${email.trim()} can now sign in`); setEmail(""); refresh(); }
    catch (err) { toast.error(errorMessage(err)); }
    setBusy(false);
  };

  const remove = async (em) => {
    try { await api.delete(`/admin/admins/${encodeURIComponent(em)}`); toast.success(`Removed ${em}`); refresh(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="space-y-8 max-w-2xl">
      <div className="rise">
        <p className="font-mono text-xs uppercase tracking-wider text-indigo-400">Access control</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Admin Access</h1>
        <p className="text-slate-400 mt-2 text-sm sm:text-base">Only these @dhwaniris.com people can sign in. Removing someone ends their session immediately.</p>
      </div>
      <div className="bg-[#0F172A] border border-slate-800 rounded-xl p-5 sm:p-6 space-y-5 rise" style={{ animationDelay: "80ms" }}>
        <form onSubmit={add} className="flex gap-2">
          <Input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@dhwaniris.com" className="bg-[#080C14] border-slate-700 h-10" />
          <Button data-testid="admin-add-button" disabled={busy} type="submit" className="h-10 bg-indigo-500 hover:bg-indigo-600 active:scale-95 transition-transform"><UserPlus className="h-4 w-4 mr-2" />Add admin</Button>
        </form>
        <ul className="divide-y divide-slate-800" data-testid="admin-list">
          {admins.map((a) => (
            <li key={a.email} data-testid="admin-row" className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm text-slate-100 break-all">{a.email}{a.email === user?.email && <span className="ml-2 font-mono text-[10px] uppercase text-indigo-300">you</span>}</p>
                <p className="text-xs text-slate-500">{a.bootstrap ? "Owner · set in server config" : `Added by ${a.added_by} · ${fmtDate(a.added_at)}`}</p>
              </div>
              {a.bootstrap ? <Lock className="h-4 w-4 text-slate-600" data-testid="admin-locked-icon" /> : a.email !== user?.email && (
                <Button data-testid="admin-remove-button" size="icon" variant="ghost" onClick={() => remove(a.email)} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></Button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
